import { createTypstCompiler } from "@myriaddreamin/typst.ts/dist/esm/compiler.mjs";
import {
  preloadRemoteFonts,
  withAccessModel,
  withPackageRegistry,
} from "@myriaddreamin/typst.ts/dist/esm/options.init.mjs";
import compiler from "@myriaddreamin/typst-ts-web-compiler/pkg/typst_ts_web_compiler_bg.wasm?url";
import {
  FetchAccessModel,
  FetchPackageRegistry,
} from "@myriaddreamin/typst.ts";
import { z } from "zod";
import { Err, Ok, Result } from "ts-results";

export const typstLayoutSchema = z.object({
  page_size: z.string().default("a4"),
  margin: z.number().default(0.5),
  logo: z.instanceof(File),
  date: z.string().default(new Date().toISOString().split("T")[0]),
  year: z.number().default(new Date().getFullYear()),
  filepaths: z.array(z.instanceof(File)).min(1),
});

export const typstLayoutTemplate = async (
  urlPath: string,
  inputJson: z.infer<typeof typstLayoutSchema>,
): Promise<Result<File, string[]>> => {
  const am = new FetchAccessModel(urlPath);
  const fontTypes = [
    "Black",
    "BlackItalic",
    "Bold",
    "BoldItalic",
    "ExtraBold",
    "ExtraBoldItalic",
    "ExtraLight",
    "ExtraLightItalic",
    "Light",
    "LightItalic",
    "Medium",
    "MediumItalic",
    "Regular",
    "RegularItalic",
    "SemiBold",
    "SemiBoldItalic",
    "Thin",
    "ThinItalic",
  ];
  const interFonts = fontTypes.map(
    (t) => `typst-templates/fonts/Inter-${t}.ttf`
  );
  const mathFonts = fontTypes.map(
    (t) => `typst-templates/fonts/STIXTwoMath-${t}.ttf`
  );

  const cc = createTypstCompiler();

  await cc.init({
    beforeBuild: [
      preloadRemoteFonts([...interFonts, ...mathFonts]),
      withAccessModel(am),
      withPackageRegistry(new FetchPackageRegistry(am)),
    ],
    getModule: () => compiler,
  })

  const e = new TextEncoder();

  cc.mapShadow(`/${inputJson.logo.name}`, new Uint8Array(await inputJson.logo.arrayBuffer()));

  await Promise.all(inputJson.filepaths.map(async (filepath) => {
    cc.mapShadow(`/${filepath.name}`, new Uint8Array(await filepath.arrayBuffer()));
  }));

  

  cc.mapShadow(
    "/data.json",
    e.encode(JSON.stringify({...inputJson, filepaths: inputJson.filepaths.map(f => f.name), logo: inputJson.logo.name}))
  );    

  const result = await cc.compile({ mainFilePath: "/main.typ", format: "pdf" });

  if (result.diagnostics || !result.result) {
    console.error(result.diagnostics)
    return Err(result?.diagnostics?.map(d => typeof d === "string" ? d : d.message) ?? ["Error compiling typst"]);
  }

  const pdfBlob = new Blob([new Uint8Array(result.result)], { type: "application/pdf" });
  const pdfFile = new File([pdfBlob], "layout.pdf", { type: "application/pdf" });

  return Ok(pdfFile);
};
